import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { cx } from 'utils';

/**
 * Modal — accessible, animated dialog rendered through a portal.
 *
 * Clean white panel over a soft slate scrim with smooth scale/fade motion.
 * Handles Escape-to-close, body scroll lock, backdrop click, and full keyboard
 * accessibility: focus moves into the panel on open, is trapped while open
 * (Tab/Shift+Tab cycle within), and is restored to the trigger on close.
 *
 * @param {boolean}  open
 * @param {() => void} onClose
 * @param {'sm'|'md'|'lg'|'xl'} [size='md']
 * @param {boolean}  [closeOnBackdrop=true]
 */
const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  className = '',
  children,
}) {
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  const hasTextTitle = typeof title === 'string' && title.length > 0;
  const hasTextDesc = typeof description === 'string' && description.length > 0;

  // Escape to close, lock background scroll, and manage focus while open.
  useEffect(() => {
    if (!open) return undefined;

    // Remember what had focus so we can restore it when the dialog closes.
    const previouslyFocused = document.activeElement;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      // Trap focus within the panel.
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll(FOCUSABLE);
      if (focusable.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the panel (first focusable element, else the panel).
    const focusTimer = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const first = panel.querySelector(FOCUSABLE);
      (first || panel).focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the element that opened the dialog.
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          {/* Scrim */}
          <motion.div
            className="absolute inset-0 bg-slate-900/35 backdrop-blur-[6px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeOnBackdrop ? onClose : undefined}
          />

          {/* Panel */}
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            aria-label={!hasTextTitle && typeof title === 'string' ? title : undefined}
            aria-labelledby={hasTextTitle ? titleId : undefined}
            aria-describedby={hasTextDesc ? descId : undefined}
            className={cx(
              // flex-col + capped height so a tall body scrolls within the
              // viewport instead of overflowing off-screen; header/footer stay pinned.
              'relative flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-3xl border border-slate-100 bg-white p-6 text-slate-700 shadow-elevated focus:outline-none sm:max-h-[calc(100vh-3rem)] sm:p-8',
              SIZES[size] || SIZES.md,
              className
            )}
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            {(title || onClose) && (
              <div className="mb-4 flex shrink-0 items-start justify-between gap-4">
                <div className="min-w-0">
                  {title && (
                    <h2 id={hasTextTitle ? titleId : undefined} className="font-display text-xl font-semibold text-slate-900">
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p id={hasTextDesc ? descId : undefined} className="mt-1 text-sm text-slate-500">
                      {description}
                    </p>
                  )}
                </div>
                {onClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close dialog"
                    className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-700 focus-ring"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M6 6l12 12M18 6L6 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Scrollable body: min-h-0 lets it shrink so overflow scrolls; the
                small horizontal padding keeps focus rings from clipping at the edge. */}
            <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 text-sm text-slate-600">
              {children}
            </div>

            {footer && (
              <div className="mt-6 flex shrink-0 items-center justify-end gap-3">{footer}</div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
